/**
 * Theme-mode context for ParlSearch (issue #159 — dark mode).
 *
 * Resolves the effective light/dark mode from the user's `theme` preference
 * ('light' | 'dark' | 'system'), builds the corresponding MUI theme, and wraps
 * children in ThemeProvider + CssBaseline.
 *
 * Persistence:
 *   - Signed-in users: the `theme` value lives in PreferencesContext (synced to
 *     the backend, so it follows them across devices).
 *   - Anonymous users: PreferencesContext already mirrors the choice to
 *     localStorage. We additionally keep a tiny standalone `parlsearch_theme`
 *     key so first paint can pick the right mode before preferences hydrate.
 *   - First visit (no stored choice): default to the OS `prefers-color-scheme`.
 */
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { usePreferences } from './PreferencesContext';
import { buildTheme } from '../theme';

const ThemeModeContext = createContext(null);

// Standalone key used purely to make the initial paint flash-free; the
// authoritative store is PreferencesContext.
const THEME_KEY = 'parlsearch_theme';

function getSystemMode() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

// Resolve a setting ('light' | 'dark' | 'system') to an effective mode.
function resolveMode(setting, systemMode) {
  if (setting === 'light' || setting === 'dark') return setting;
  return systemMode; // 'system' or anything unexpected
}

export function ThemeModeProvider({ children }) {
  const { preferences, updatePreferences } = usePreferences();

  // Track the live OS preference so 'system' mode reacts to changes.
  const [systemMode, setSystemMode] = useState(getSystemMode);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemMode(e.matches ? 'dark' : 'light');
    // addEventListener is the modern API; addListener is the legacy fallback.
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  // The setting comes from preferences (which itself falls back to localStorage
  // for anonymous users and to 'system' on first visit).
  const themeSetting = preferences?.theme || 'system';
  const mode = resolveMode(themeSetting, systemMode);

  // Mirror the resolved setting to the standalone key + a data attribute so the
  // initial paint (and any non-MUI surfaces, e.g. Leaflet/Recharts) can read it.
  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, themeSetting);
    } catch (e) {
      /* ignore quota / privacy-mode errors */
    }
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', mode);
    }
  }, [themeSetting, mode]);

  const setThemeSetting = useCallback((setting) => {
    updatePreferences({ theme: setting });
  }, [updatePreferences]);

  // Toggle flips between explicit light/dark based on what's currently showing.
  const toggleMode = useCallback(() => {
    setThemeSetting(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setThemeSetting]);

  const theme = useMemo(() => buildTheme(mode), [mode]);

  const value = useMemo(() => ({
    mode,           // resolved 'light' | 'dark'
    themeSetting,   // 'light' | 'dark' | 'system'
    setThemeSetting,
    toggleMode,
  }), [mode, themeSetting, setThemeSetting, toggleMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
}

export default ThemeModeContext;
